// src/tabs/calendar/ListeView.jsx — PR #169c (3e sous-onglet 📋 Liste)
//
// Vue Liste : tableau filtrable + triable des réservations + blocks direct
// interne FBZ (guest_first_name présent). Cohérent ADR-105 (kind discriminator
// onSelectItem) + ADR-106 (source agrégation unifiée).
//
// Source de données : reservations (props.filtered) + blocksByLogement (Map).
// Filtres globaux du header CalendarPanel restent actifs en amont (filtered
// déjà filtré par logement/source/searchQuery/statut).
//
// Filtres locaux ListeView (au-dessus de la table) :
//   - multi-plateforme checkboxes Airbnb / Booking / Direct / Block
//     (vs select simple Tableau — UX différenciée Liste vs Tableau)
//
// Tri : clic header colonne → ASC, re-clic → DESC. Tri défaut start_date DESC.
//
// Persistance state local (sortKey + sortDir + filterPlatforms) :
//   localStorage 'fbz-liste-state' via safeListeState() (anti-corruption).
//
// ─────────────────────────────────────────────────────────────────────
// RGPD — `rows[]` contient guest_email (PII).
//   INTERDIT `console.log(rows)`, `console.error(rows)`, ou tout passage de
//   l'objet row complet à un logger / Sentry. En cas d'erreur dans useMemo /
//   handlers, logger uniquement `e.message` (chaîne courte sans PII).
//   (M-3 amend v1.1 plan PR #169c.)
// ─────────────────────────────────────────────────────────────────────

import { useState, useMemo, useEffect, useCallback } from "react"
import { ChevronsUpDown, ArrowUp, ArrowDown, CalendarSearch } from "lucide-react"
import { DS } from "../../lib/ui"
import {
  buildUnifiedRows,
  filterRows,
  sortRows,
  safeListeState,
  LISTE_PLATFORMS,
} from "./_liste-helpers"
import { SOURCE_BG, SOURCE_BG_FALLBACK } from "./_source-colors"

const LISTE_STATE_LS_KEY = "fbz-liste-state"

// DA L2 — noms de source lisibles (plus de code court AB/BK/DR/BL).
//   « block » = BLOCAGES (source réelle utilisée par le filtre, conservée) →
//   libellé « Blocage », badge gris srcBloque.
const SOURCE_LABEL = {
  airbnb: "Airbnb",
  booking: "Booking",
  direct: "Direct",
  block: "Blocage",
}

// Pastilles statut sémantiques — tokens DA (success/warning/danger).
const STATUS_STYLES = {
  confirmed: { bg: DS.successBg, color: DS.successText, label: "Confirmée" },
  pending: { bg: DS.warningBg, color: DS.warningText, label: "En attente" },
  cancelled: { bg: DS.dangerBg, color: DS.dangerText, label: "Annulée", strike: true },
}

// 9 colonnes définies une fois pour le rendu header (cohérent LISTE_SORT_KEYS).
const COLUMNS = [
  { key: "logement_id", label: "Logement", width: 140 },
  { key: "guest_first_name", label: "Voyageur", width: 160 },
  { key: "created_at", label: "Date résa", width: 90 },
  { key: "start_date", label: "Check-in", width: 90 },
  { key: "end_date", label: "Check-out", width: 90 },
  { key: "nights", label: "Nuits", width: 50, align: "right" },
  { key: "guest_count", label: "Pers.", width: 50, align: "right" },
  { key: "source", label: "Plateforme", width: 90 },
  { key: "status", label: "Statut", width: 100 },
]

function fmtDateShort(iso) {
  if (!iso) return "—"
  // YYYY-MM-DD → DD/MM
  const [y, m, d] = iso.split("-")
  if (!y || !m || !d) return iso
  return `${d}/${m}`
}

function fmtDateMid(iso) {
  if (!iso) return "—"
  const [y, m, d] = iso.split("-")
  if (!y || !m || !d) return iso
  return `${d}/${m}/${y.slice(2)}`
}

/**
 * Renvoie la couleur source pour bordure left ligne.
 * @param {string|null} source
 */
function getSourceColor(source) {
  // DA L2 — « block » = BLOCAGE → gris srcBloque (badge + bordure left cohérents).
  // Couleur de source résolue localement (ne PAS modifier _source-colors.js,
  // partagé avec TableauView/Gantt).
  if (source === "block") return DS.srcBloque
  return SOURCE_BG[source] || SOURCE_BG_FALLBACK
}

function StatutBadge({ status }) {
  if (!status) return <span style={{ color: "#aaa", fontSize: 11 }}>—</span>
  const s = STATUS_STYLES[status] || {
    bg: "#e5e7eb",
    color: "#374151",
    label: status,
  }
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 9px",
        borderRadius: 999,
        background: s.bg,
        color: s.color,
        fontSize: 10,
        fontWeight: 700,
        textDecoration: s.strike ? "line-through" : "none",
        whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </span>
  )
}

// Badge source détaché DA — pastille pleine couleur + nom lisible.
//   Airbnb corail / Booking marine / Direct vert / Blocage gris (srcBloque).
//   Sur le gris clair, texte marine foncé pour le contraste (sinon blanc).
function PlatformBadge({ source }) {
  const color = getSourceColor(source)
  const label = SOURCE_LABEL[source] || source || "—"
  const fg = source === "block" ? DS.marineDeep : "#fff"
  return (
    <span
      title={label}
      style={{
        display: "inline-block",
        fontSize: 10,
        fontWeight: 700,
        color: fg,
        background: color,
        borderRadius: 999,
        padding: "2px 9px",
        letterSpacing: 0.2,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  )
}

function SortIndicator({ active, dir }) {
  if (!active) {
    return (
      <ChevronsUpDown size={12} strokeWidth={2} color={DS.textMuted}
        style={{ marginLeft: 4, verticalAlign: "middle" }} aria-hidden="true" />
    )
  }
  return dir === "asc" ? (
    <ArrowUp size={12} strokeWidth={2.4} color={DS.corail} style={{ marginLeft: 4, verticalAlign: "middle" }} aria-hidden="true" />
  ) : (
    <ArrowDown size={12} strokeWidth={2.4} color={DS.corail} style={{ marginLeft: 4, verticalAlign: "middle" }} aria-hidden="true" />
  )
}

function EmptyState() {
  return (
    <tr>
      <td
        colSpan={COLUMNS.length}
        style={{
          padding: "40px 20px",
          textAlign: "center",
          color: "#666",
          fontFamily: DS.fontBody,
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}>
          <CalendarSearch size={28} strokeWidth={1.6} color={DS.ciel} aria-hidden="true" />
        </div>
        <div style={{ fontFamily: DS.fontDisplay, fontSize: 14, fontWeight: 600, color: DS.marineDark }}>
          Aucune réservation trouvée
        </div>
        <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
          Ajuste tes filtres.
        </div>
      </td>
    </tr>
  )
}

/**
 * Vue Liste — tableau filtrable + triable.
 *
 * @param {object} props
 * @param {Array<object>} props.filtered — réservations déjà filtrées
 *   (logement / source / searchQuery / filterStatut) par CalendarPanel.
 * @param {Map<string, Array<object>>|null} props.blocksByLogement — Map blocks.
 * @param {Map<string, string>} props.logementsById — Map id → nom (labels).
 * @param {(payload: {id: string, kind: 'reservation'|'pricing_block'}) => void} props.onSelectItem
 *   F-5 ADR-105 — ouvre ResaV2Modal kind-aware.
 * @param {(count: number) => void} [props.onCountChange] — H-2 amend v1.1.
 *   Callback notifié à chaque changement du count affiché (footer
 *   CalendarPanel utilise pour aligner sur lignes Liste vs résas Tableau).
 */
export default function ListeView({
  filtered,
  blocksByLogement,
  logementsById,
  onSelectItem,
  onCountChange,
}) {
  // ── State local persisté localStorage 'fbz-liste-state' ──────────────
  const [listeState, setListeState] = useState(() => {
    try {
      return safeListeState(localStorage.getItem(LISTE_STATE_LS_KEY))
    } catch (_) {
      return safeListeState(null)
    }
  })

  // Persistance LS (sortKey + sortDir + filterPlatforms)
  useEffect(() => {
    try {
      localStorage.setItem(LISTE_STATE_LS_KEY, JSON.stringify(listeState))
    } catch (_) {
      // LS quota / private mode → silent
    }
  }, [listeState])

  const { sortKey, sortDir, filterPlatforms } = listeState

  // ── Build → filter local → sort ──────────────────────────────────────
  //
  // PII WARNING : ne PAS console.log(unifiedRows / filteredRows / sortedRows)
  // → rows[].guest_email présent. Cf. JSDoc en tête fichier.
  const unifiedRows = useMemo(() => {
    try {
      return buildUnifiedRows(filtered, blocksByLogement)
    } catch (e) {
      // Logger message court uniquement, JAMAIS l'objet row.
      console.error("[ListeView] buildUnifiedRows error:", e?.message || "unknown")
      return []
    }
  }, [filtered, blocksByLogement])

  const filteredRows = useMemo(() => {
    try {
      return filterRows(unifiedRows, {
        filterPlatforms: new Set(filterPlatforms),
      })
    } catch (e) {
      console.error("[ListeView] filterRows error:", e?.message || "unknown")
      return unifiedRows
    }
  }, [unifiedRows, filterPlatforms])

  const sortedRows = useMemo(() => {
    try {
      return sortRows(filteredRows, sortKey, sortDir)
    } catch (e) {
      console.error("[ListeView] sortRows error:", e?.message || "unknown")
      return filteredRows
    }
  }, [filteredRows, sortKey, sortDir])

  // H-2 amend v1.1 — notifie le parent à chaque variation count.
  useEffect(() => {
    if (typeof onCountChange === "function") {
      try {
        onCountChange(sortedRows.length)
      } catch (_) {
        /* swallow */
      }
    }
  }, [sortedRows.length, onCountChange])

  // ── Handlers ─────────────────────────────────────────────────────────
  const handleHeaderClick = useCallback(
    (colKey) => {
      setListeState((prev) => {
        if (prev.sortKey === colKey) {
          return { ...prev, sortDir: prev.sortDir === "asc" ? "desc" : "asc" }
        }
        return { ...prev, sortKey: colKey, sortDir: "asc" }
      })
    },
    [],
  )

  const togglePlatform = useCallback((platform) => {
    setListeState((prev) => {
      const set = new Set(prev.filterPlatforms)
      if (set.has(platform)) {
        set.delete(platform)
      } else {
        set.add(platform)
      }
      return { ...prev, filterPlatforms: Array.from(set) }
    })
  }, [])

  const handleRowClick = useCallback(
    (row) => {
      if (typeof onSelectItem === "function") {
        onSelectItem({ id: row.id, kind: row.kind })
      }
    },
    [onSelectItem],
  )

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: DS.fontBody }}>
      {/* Filtres locaux ListeView : multi-plateforme checkboxes */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
          padding: "8px 4px",
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: 11, color: "#666", fontWeight: 600 }}>
          Plateformes :
        </span>
        {LISTE_PLATFORMS.map((p) => {
          const checked = filterPlatforms.includes(p)
          return (
            <label
              key={p}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 11,
                cursor: "pointer",
                userSelect: "none",
                padding: "3px 8px",
                borderRadius: 5,
                background: checked ? "#eef2f7" : "transparent",
                border: `1px solid ${checked ? "#dde3ec" : "transparent"}`,
              }}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => togglePlatform(p)}
                style={{ cursor: "pointer" }}
              />
              <PlatformBadge source={p} />
            </label>
          )
        })}
        <span
          style={{
            marginLeft: "auto",
            fontSize: 11,
            color: "#888",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          Trié par{" "}
          <strong>{COLUMNS.find((c) => c.key === sortKey)?.label || sortKey}</strong>
          {sortDir === "asc" ? (
            <ArrowUp size={11} strokeWidth={2.4} color={DS.corail} aria-hidden="true" />
          ) : (
            <ArrowDown size={11} strokeWidth={2.4} color={DS.corail} aria-hidden="true" />
          )}
        </span>
      </div>

      {/* Table sticky header — fbz-table-wrap : scroll-x encadré + affordance
          mobile (gradient < 768). overflowY/maxHeight conservés inline pour le
          header sticky. */}
      <div className="fbz-table-wrap" style={{ overflowX: "auto", maxHeight: "70vh", overflowY: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: 0,
            fontSize: 12,
            fontFamily: DS.fontBody,
          }}
        >
          <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
            <tr>
              {COLUMNS.map((col) => {
                const active = col.key === sortKey
                return (
                  <th
                    key={col.key}
                    onClick={() => handleHeaderClick(col.key)}
                    style={{
                      width: col.width,
                      textAlign: col.align || "left",
                      padding: "8px 10px",
                      background: DS.white,
                      borderBottom: `2px solid ${DS.border}`,
                      fontWeight: 700,
                      color: active ? DS.corail : DS.marine,
                      cursor: "pointer",
                      userSelect: "none",
                      whiteSpace: "nowrap",
                      fontSize: 11,
                    }}
                    title={`Trier par ${col.label}`}
                  >
                    {col.label}
                    <SortIndicator active={active} dir={sortDir} />
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 && <EmptyState />}
            {sortedRows.map((r) => {
              const logementLabel =
                logementsById?.get?.(r.logement_id) || r.logement_id || "—"
              const guestName =
                [r.guest_first_name, r.guest_last_name].filter(Boolean).join(" ") || "—"
              const sourceColor = getSourceColor(r.source)
              return (
                <tr
                  key={`${r.kind}-${r.id}`}
                  className="fbz-rowhover"
                  onClick={() => handleRowClick(r)}
                  style={{
                    cursor: "pointer",
                    borderBottom: "1px solid #eef2f7",
                  }}
                >
                  <td
                    style={{
                      padding: "7px 10px",
                      borderLeft: `3px solid ${sourceColor}`,
                      fontFamily: DS.fontDisplay,
                      fontWeight: 600,
                      color: DS.navy,
                    }}
                  >
                    {logementLabel}
                  </td>
                  <td style={{ padding: "7px 10px" }}>{guestName}</td>
                  <td style={{ padding: "7px 10px", color: "#666" }}>
                    {fmtDateMid(r.created_at)}
                  </td>
                  <td style={{ padding: "7px 10px" }}>{fmtDateShort(r.start_date)}</td>
                  <td style={{ padding: "7px 10px" }}>{fmtDateShort(r.end_date)}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right", fontFamily: DS.fontDisplay }}>
                    {r.nights || "—"}
                  </td>
                  <td style={{ padding: "7px 10px", textAlign: "right", fontFamily: DS.fontDisplay, color: "#666" }}>
                    {r.guest_count || "—"}
                  </td>
                  <td style={{ padding: "7px 10px" }}>
                    <PlatformBadge source={r.source} />
                  </td>
                  <td style={{ padding: "7px 10px" }}>
                    <StatutBadge status={r.status} />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Footer count (Liste-specific, complète celui CalendarPanel via onCountChange) */}
      <div
        style={{
          marginTop: 8,
          fontSize: 11,
          color: "#888",
          textAlign: "right",
        }}
      >
        {sortedRows.length} ligne{sortedRows.length > 1 ? "s" : ""} ·{" "}
        {unifiedRows.length} sources total
      </div>
    </div>
  )
}
